import type { Key } from "react";

import {
  Autocomplete,
  EmptyState,
  Label,
  ListBox,
  SearchField,
  Tag,
  TagGroup,
  useFilter,
} from "@heroui/react";
import { useMemo } from "react";

export type PickerOption = { id: string; name: string };

/**
 * A field that holds several choices as tags and finds them by typing. With `onSearch` the options come from the
 * server for what is typed (they are not filtered again here); without it the given options are filtered locally.
 * `selected` carries the names of what is already chosen, so tags keep their names when the options change.
 */
export function MultiPicker({
  label,
  placeholder,
  options,
  selected,
  onChange,
  onSearch,
  isLoading,
  isInvalid,
  errorText,
}: {
  label: string;
  placeholder: string;
  options: PickerOption[];
  selected: PickerOption[];
  onChange: (next: PickerOption[]) => void;
  onSearch?: (text: string) => void;
  isLoading?: boolean;
  isInvalid?: boolean;
  errorText?: string;
}) {
  const { contains } = useFilter({ sensitivity: "base" });
  const known = useMemo(() => {
    const map = new Map<string, PickerOption>();

    for (const option of options) map.set(option.id, option);
    for (const option of selected) map.set(option.id, option);

    return map;
  }, [options, selected]);
  // The list shows the options plus anything already chosen, so a chosen item is always in the collection.
  const items = useMemo(() => Array.from(known.values()), [known]);
  const keys = selected.map((option) => option.id);

  const apply = (next: Key[]) =>
    onChange(
      next
        .map((key) => known.get(String(key)))
        .filter((option): option is PickerOption => option !== undefined),
    );

  return (
    <Autocomplete
      className="flex flex-col gap-1.5"
      isInvalid={isInvalid}
      placeholder={placeholder}
      selectionMode="multiple"
      value={keys}
      variant="secondary"
      onChange={(next) => apply((next as Key[]) ?? [])}
    >
      <Label>{label}</Label>
      <Autocomplete.Trigger>
        <Autocomplete.Value>
          {({ defaultChildren, isPlaceholder }) =>
            isPlaceholder || selected.length === 0 ? (
              defaultChildren
            ) : (
              <TagGroup
                aria-label={`Selected ${label.toLowerCase()}`}
                size="sm"
                onRemove={(removed) =>
                  apply(keys.filter((key) => !removed.has(key)))
                }
              >
                <TagGroup.List className="flex flex-wrap gap-1">
                  {selected.map((option) => (
                    <Tag key={option.id} id={option.id} textValue={option.name}>
                      {option.name}
                      <Tag.RemoveButton />
                    </Tag>
                  ))}
                </TagGroup.List>
              </TagGroup>
            )
          }
        </Autocomplete.Value>
        <Autocomplete.Indicator />
      </Autocomplete.Trigger>
      {errorText ? <p className="text-(--danger) text-sm">{errorText}</p> : null}
      <Autocomplete.Popover>
        <Autocomplete.Filter
          filter={onSearch ? () => true : contains}
          onInputChange={onSearch}
        >
          <SearchField
            autoFocus
            aria-label={`Search ${label.toLowerCase()}`}
            name="search"
            variant="secondary"
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder="Search" />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
          <ListBox
            items={items}
            renderEmptyState={() => (
              <EmptyState className="py-4 text-sm">
                {isLoading ? "Loading..." : "No options available"}
              </EmptyState>
            )}
          >
            {(option) => (
              <ListBox.Item
                key={option.id}
                id={option.id}
                textValue={option.name}
              >
                {option.name}
                <ListBox.ItemIndicator />
              </ListBox.Item>
            )}
          </ListBox>
        </Autocomplete.Filter>
      </Autocomplete.Popover>
    </Autocomplete>
  );
}
